import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { OrderedMap } from 'immutable';
import kebabCase from 'lodash/kebabCase';
import cx from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';

import Location from 'components/search/curated-selection/location';

import CuratedLocations from 'models/curated-locations';
import Search from 'models/search';
import Brand from 'models/brand';

import {
  CURATED_IMPRESSION,
  CURATED_DISMISSED,
  CURATED_CLOSE_BUTTON_IMPRESSION,
  CURATED_SHOW_ALL_IMPRESSION,
  CURATED_SHOW_ALL_CLICK,
} from 'lib/analytics/events/index';

const propTypes = {
  curatedLocations: PropTypes.instanceOf(CuratedLocations).isRequired,
  locations: PropTypes.instanceOf(OrderedMap).isRequired,
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  dirty: PropTypes.bool.isRequired,
  appContext: PropTypes.string.isRequired,
  changeLocation: PropTypes.func.isRequired,
  curationDismissed: PropTypes.bool.isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  dismissCuration: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};

const defaultProps = {

};

class CuratedSelection extends Component {
  static renderLoadingIndicator() {
    return (
      <div className="row background-color-off-color padding-bottom-xs-80 padding-bottom-sm-0">
        <div
          className="padding-top-40 text-align-center text-color-blue text-size-sm-18 text-size-xs-10 text-weight-medium"
        >
          LOADING RESULTS...
        </div>
      </div>
    );
  }

  constructor(props) {
    super(props);

    this.close = this.close.bind(this);
    this.cancelClick = this.cancelClick.bind(this);
    this.trackView = this.trackView.bind(this);
    this.showAll = this.showAll.bind(this);
    this.onCloseButtonVisibilityChange = this.onCloseButtonVisibilityChange.bind(this);
    this.onShowAllButtonVisibilityChange = this.onShowAllButtonVisibilityChange.bind(this);
  }

  componentDidMount() {
    this.trackView();
  }

  componentDidUpdate(prevProps) {
    const currentResults = this.props.curatedLocations.forDisplay(this.props.locations, this.props.currentSearch);
    const prevResults = prevProps.curatedLocations.forDisplay(prevProps.locations, prevProps.currentSearch);
    if (currentResults !== prevResults) { this.trackView(); }
  }

  trackView() {
    if (this.display) {
      const results = this.props.curatedLocations.forDisplay(this.props.locations, this.props.currentSearch);
      const properties = {};
      results.forEach((v, k) => {
        properties[k] = {
          price: v.getQuote().price,
          distance: v.distance,
          score: v.getQuote().curationScore,
          locationID: v.id,
        };
      });
      this.props.trackEvent({
        ...CURATED_IMPRESSION,
        properties: {
          ...properties,
          CuratedStyle: 'Map',
        },
      });
    }
  }

  get display() {
    const { curatedLocations, locations, currentSearch, curationDismissed, brand } = this.props;

    return (
      brand.curationEligible &&
      !curationDismissed &&
      curatedLocations.shouldDisplay(locations, currentSearch, brand) &&
      !currentSearch.selectedLocationId
    );
  }

  close() {
    this.props.dismissCuration({ save: true });
    this.props.trackEvent({ ...CURATED_DISMISSED });
  }

  showAll() {
    this.props.dismissCuration({ save: true });
    this.props.trackEvent({
      ...CURATED_SHOW_ALL_CLICK,
      properties: {
        LocationCount: this.props.locations.size,
      },
    });
  }

  onCloseButtonVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent({ ...CURATED_CLOSE_BUTTON_IMPRESSION });
    }
  }

  onShowAllButtonVisibilityChange(isVisible) {
    if (isVisible) {
      this.props.trackEvent({
        ...CURATED_SHOW_ALL_IMPRESSION,
        properties: {
          LocationCount: this.props.locations.size,
        },
      });
    }
  }

  cancelClick(e) {
    e.stopPropagation();
  }

  renderCuratedResults() {
    const {
      dirty,
      changeLocation,
      dismissCuration,
      trackEvent,
      curatedLocations,
      locations,
      currentSearch,
      appContext,
      brand,
    } = this.props;
    if (dirty) { return CuratedSelection.renderLoadingIndicator(); }

    const displayLocations = curatedLocations.forDisplay(locations, currentSearch);
    const locationCards = displayLocations.entrySeq().map(o => (
      <Location
        key={`cl-${kebabCase(o[0])}`}
        label={o[0]}
        location={o[1]}
        changeLocation={changeLocation}
        dismissCuration={dismissCuration}
        trackEvent={trackEvent}
        locationCount={displayLocations.size}
        currentSearch={currentSearch}
        appContext={appContext}
        routingStyle={brand.routingStyle}
      />
    ));
    const modalBodyClasses = cx({
      'col-sm-8': displayLocations.size === 3,
      'col-sm-offset-2': displayLocations.size === 3,
      'col-sm-6': displayLocations.size <= 2,
      'col-sm-offset-3': displayLocations.size <= 2,
      'col-gutter-xs-0': true,
      'col-gutter-sm-30': true,
      'background-color-off-white': true,
      'padding-top-xs-15': true,
      'padding-bottom-10': true,
      'border-radius-4': true,
    });

    return (
      <div className="row">
        <div className={modalBodyClasses} onClick={this.cancelClick}>
          <div className="row margin-bottom-xs-5 margin-vertical-sm-10">
            <div className="col-xs-10 text-weight-black text-size-sm-20 text-color-dark-slate col-gutter-left-xs-30 col-gutter-left-sm-15">
              Suggested Spaces for You
            </div>
            <VisibilitySensor onChange={this.onCloseButtonVisibilityChange} partialVisibility>
              <div className="col-xs-2 text-align-right col-gutter-right-xs-30 col-gutter-right-sm-15">
                <div className="visible-xs iconified-font iconified-x-border text-size-18" onClick={this.close} />
                <div className="hidden-xs iconified-font iconified-x padding-left-10 padding-bottom-5 text-size-18 text-color-blue text-color-hover-blue clickable" onClick={this.close} />
              </div>
            </VisibilitySensor>
          </div>
          <div className="row row-centered">
            {locationCards}
          </div>

          <div className="row">
            <div className="col-xs-12 text-align-xs-center text-align-sm-right">
              <VisibilitySensor onChange={this.onShowAllButtonVisibilityChange} partialVisibility>
                <span className="text-color-blue text-color-hover-blue text-size-12 clickable padding-vertical-10 display-inline-block" onClick={this.showAll}>
                  <span className="iconified-font iconified-bold-plus margin-right-5" /> SHOW ME ALL { locations.size } FACILITIES
                </span>
              </VisibilitySensor>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (!this.display) { return null; }

    return (
      <div className="curated-selection-modal background-color-xs-off-white background-color-sm-transparent-black-50" onClick={this.close}>
        <div className="curated-selection-wrapper">
          <div className="container margin-top-sm-40">
            { this.renderCuratedResults() }
          </div>
        </div>
      </div>
    );
  }
}

CuratedSelection.propTypes = propTypes;
CuratedSelection.defaultProps = defaultProps;

export default CuratedSelection;

